import React, { useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { Alert, Button, Menu, MenuItem, Snackbar } from "@mui/material";
import dayjs from "dayjs";

import * as Sentry from "@sentry/react";
import { useShowNetworkFailureToast } from "../NetworkFailureToast";
import { useTrueBizApi } from "../../api";
import { useIdentity } from "../IdentityProvider";

export default function ExportSearchHistoryButton({
  linkText = "Export",
}: {
  linkText?: string;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [working, setWorking] = useState(false);
  const [successToastOpen, setSuccessToastOpen] = useState(false);
  const showErrorToast = useShowNetworkFailureToast();
  const api = useTrueBizApi();

  const { impersonatorUserId } = useIdentity();

  const createExport = async (
    minDate: string | null,
    maxDate: string | null
  ) => {
    setWorking(true);
    try {
      await api.initiateSearchHistoryExport({
        minDate,
        maxDate,
        emailOnSuccess: true,
        impersonatorUserId: impersonatorUserId || null,
      });
      setSuccessToastOpen(true);
    } catch (e) {
      Sentry.captureException(e);
      showErrorToast("Data export failed, please try again shortly.");
    } finally {
      setWorking(false);
      setAnchorEl(null);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleMenuClick}
      >
        <DownloadIcon sx={{ ml: -0.5, mr: 1 }} /> {linkText}
      </Button>
      <Menu
        id="basic-menu"
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      >
        <MenuItem
          disabled={working}
          onClick={() =>
            createExport(dayjs().subtract(90, "day").format("YYYY-MM-DD"), null)
          }
        >
          Last 90 days
        </MenuItem>
        <MenuItem
          disabled={working}
          onClick={() =>
            createExport(
              dayjs().subtract(6, "month").format("YYYY-MM-DD"),
              null
            )
          }
        >
          Last 6 months
        </MenuItem>
        <MenuItem
          disabled={working}
          onClick={() =>
            createExport(dayjs().subtract(1, "year").format("YYYY-MM-DD"), null)
          }
        >
          Last year
        </MenuItem>
      </Menu>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successToastOpen}
        autoHideDuration={8000}
        onClose={() => setSuccessToastOpen(false)}
      >
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
          Export initiated. You will receive an email with a link to download
          the file once it is ready.
        </Alert>
      </Snackbar>
    </>
  );
}
